import { enqueueSnackbar } from '~/utils/notifications';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { DicomStructureSet } from '~/domain/dicom/dicomstructureset';
import { Patient } from '~/domain/patient/patient';
import { OperationPayload, StepPayload, StructureReference } from './types';
import { validateResultNameFormat } from './validationUtils';
import { buildFormulaFromSteps } from './booleanFormula';

/** One step's operand selection as the UI hands it over. */
export type StepOperandSelection = StepPayload<RoiReadModel>;

/**
 * The DICOM set behind the read model. Resolved through the patient aggregate,
 * not patient.dataSet.series: the series list only records ingested DICOM, so a
 * set created in-session (copy-to-new, "Create Structure Set") is missing there
 * and every one of its operands would fail to resolve.
 */
function findMatchingDicomStructureSet(
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): DicomStructureSet | null {
    return (
        patient.getStructureSetWithSopInstanceUid(currentStructureSet.dicomUid)
            ?.dicomStructureSet ?? null
    );
}

/**
 * Converts a ROI read model to a structure reference by its ROI number in the
 * current set, or null when that number is not there. Never by name: DICOM
 * makes only ROI numbers unique, so a name can match a different structure.
 */
export function roiReadModelToStructureReference(
    roiModel: { roiNumber: number },
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StructureReference | null {
    const structureSet = findMatchingDicomStructureSet(
        patient,
        currentStructureSet
    );
    if (!structureSet) {
        return null;
    }
    const roi = structureSet.rois.get(roiModel.roiNumber);
    return roi ? { roi, structureSet } : null;
}

type ConversionErrorReporter = (
    message: string,
    variant: 'error' | 'warning'
) => void;

/**
 * Converts a UI step to a StepPayload. A conversion failure returns null,
 * and the reporter says why.
 */
function convertStepToPayload(
    step: StepOperandSelection,
    stepIndex: number,
    patient: Patient,
    currentStructureSet: StructureSetReadModel,
    onError: ConversionErrorReporter
): StepPayload | null {
    // Every operand must resolve. Dropping the ones that don't would run a
    // narrower operation than the user asked for, and the result carries no
    // sign that an operand went missing.
    const rightRefs: StructureReference[] = [];
    const unresolved: string[] = [];
    for (const operand of step.rightOperands) {
        const ref = roiReadModelToStructureReference(
            operand,
            patient,
            currentStructureSet
        );
        if (ref) {
            rightRefs.push(ref);
        } else {
            unresolved.push(operand.name);
        }
    }
    if (unresolved.length > 0 || rightRefs.length === 0) {
        onError(
            `Boolean operation failed: could not resolve right operand(s) for step ${
                stepIndex + 1
            } (${unresolved.join(', ') || 'unknown'})`,
            'error'
        );
        return null;
    }

    if (stepIndex === 0) {
        if (step.leftOperand === 'previous-result') {
            onError('First structure is required', 'warning');
            return null;
        }
        const leftRef = roiReadModelToStructureReference(
            step.leftOperand,
            patient,
            currentStructureSet
        );
        if (!leftRef) {
            onError(
                `Structure "${step.leftOperand.name}" (ROI ${step.leftOperand.roiNumber}) not found`,
                'error'
            );
            return null;
        }

        return {
            leftOperand: leftRef,
            rightOperands: rightRefs,
            operation: step.operation,
        };
    }

    return {
        leftOperand: 'previous-result',
        rightOperands: rightRefs,
        operation: step.operation,
    };
}

/**
 * Resolves UI steps into step payloads. Returns null, and reports why, at
 * the first step that does not resolve.
 */
export function buildStepPayloads(
    steps: StepOperandSelection[],
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): StepPayload[] | null {
    const payloadSteps: StepPayload[] = [];
    for (let i = 0; i < steps.length; i++) {
        const stepPayload = convertStepToPayload(
            steps[i]!,
            i,
            patient,
            currentStructureSet,
            (message, variant) => enqueueSnackbar(message, { variant })
        );
        if (!stepPayload) {
            return null;
        }
        payloadSteps.push(stepPayload);
    }
    return payloadSteps;
}

/**
 * Builds an operation payload from UI steps with validation.
 */
export function buildPayload(
    steps: StepOperandSelection[],
    resultName: string,
    isValid: boolean,
    patient: Patient,
    currentStructureSet: StructureSetReadModel
): OperationPayload | null {
    const nameValidation = validateResultNameFormat(resultName);
    if (!nameValidation.valid) {
        enqueueSnackbar(nameValidation.error, {
            variant: 'warning',
        });
        return null;
    }

    if (!isValid) {
        enqueueSnackbar('Please fill in all required fields', {
            variant: 'warning',
        });
        return null;
    }

    const payloadSteps = buildStepPayloads(steps, patient, currentStructureSet);
    if (!payloadSteps) {
        return null;
    }

    return {
        steps: payloadSteps,
        resultName: resultName.trim(),
        formulaText: buildFormulaFromSteps(payloadSteps),
    };
}
